import { Head, usePage } from "@inertiajs/react"
import PublicLayout from "@/components/PublicLayout"
import type { PageProps } from "@/types/inertia"
import { useVisitorTimezone, dateTimeInZone } from "@/lib/slot-times"

interface Props {
  booking: {
    starts_at: string
    london_label: string
    contact_method: "mobile" | "whatsapp"
    phone: string
    status: string
  }
  cancel_token: string | null
}

export default function GiselaBookConfirmed({ booking, cancel_token }: Props) {
  const { props } = usePage<PageProps>()
  const t = props.translations.consultation
  const locale = props.current_locale || "en"
  const visitorTz = useVisitorTimezone()

  const methodLine = (
    booking.contact_method === "whatsapp" ? t.confirmed.method_whatsapp : t.confirmed.method_mobile
  ).replace("%{phone}", booking.phone)

  return (
    <>
      <Head title={t.confirmed.meta_title}>
        <meta name="robots" content="noindex" />
        <meta name="description" content={t.confirmed.body} />
        <meta property="og:title" content={t.confirmed.meta_title} />
        <meta property="og:description" content={t.confirmed.body} />
      </Head>
      <PublicLayout>
        <section className="bg-teal pt-32 pb-16 text-white">
          <div className="mx-auto max-w-2xl px-6 lg:px-8">
            <h1 className="text-white">{t.confirmed.heading}</h1>
            <p className="mt-4 text-lg text-white/80">{t.confirmed.body}</p>
          </div>
        </section>

        <section className="bg-cream py-16">
          <div className="mx-auto max-w-2xl px-6 lg:px-8">
            <div className="rounded-2xl bg-white p-8 ring-1 ring-hairline">
              <p className="text-lg">
                <strong>{booking.london_label}</strong>{" "}
                <span className="text-ink-muted">({t.confirmed.uk_time})</span>
              </p>
              {visitorTz && (
                <p className="mt-1 text-ink-muted">
                  {dateTimeInZone(booking.starts_at, visitorTz, locale)} ({t.confirmed.your_time},{" "}
                  {visitorTz})
                </p>
              )}
              <p className="mt-4 text-ink-body">{methodLine}</p>
            </div>

            {cancel_token && (
              <p className="mt-8 text-sm text-ink-muted">
                {t.confirmed.cancel_note}{" "}
                <a href={`/book/cancel/${cancel_token}`}>{t.confirmed.cancel_link}</a>
              </p>
            )}

            <p className="mt-4 text-sm">
              <a href="/">{t.confirmed.back_home}</a>
            </p>
          </div>
        </section>
      </PublicLayout>
    </>
  )
}
